import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaGithub } from 'react-icons/fa';

const HomePage = () => {
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [expandedSections, setExpandedSections] = useState({
    cite: false,
    overview: false,
    acknowledgement: false
  });
  const images = [
    '/images/homepage_2.png',
    '/images/homepage_3.png',
    '/images/homepage_4.png'
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, 3000);

    return () => clearInterval(timer);
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      {/* Red Section including header and content */}
      <div className="bg-red-800" style={{ height: '400px' }}>
        {/* Header */}
        <header className="text-white py-2">
          <div className=" px-4 flex justify-start items-center">
            <a href="/" className="text-xl  mr-10">Home</a>
            <a Link href="/about" className="text-xl  mr-4">About ▼</a>
          </div>
        </header>

        {/* Main Content in Red Section */}
        <div className="container mx-auto px-4 pt-8">
          <div className="text-white text-center max-w-4xl mx-auto">
            <h1 className="text-5xl mb-6 font-inter">
              Welcome to the Catalysis-Hub
              <br />
              Experimental Database
            </h1>
            <p className="text-xl mb-12 font-semibold">
              Explore our collection of SUNCAT experimental catalysis data.
              Access information on publications, catalyst properties and performance,
              and interact with catalyst characterization and testing data.
            </p>
          </div>
        </div>
      </div>

      {/* Centered Image - Positioned to overlap */}
      <div className="relative z-10" style={{ marginTop: '-100px', marginBottom: '-100px' }}>
        <div className="max-w-5xl mx-auto px-4">
          <div className="bg-white rounded-lg p-4">
            <div className="h-64 flex items-center justify-center overflow-hidden"> {/* Fixed height container */}
              <img 
                src={images[currentImageIndex]}
                alt={`Catalysis Hub Overview ${currentImageIndex + 1}`}
                className="max-h-full w-auto object-contain"
                style={{ 
                  transition: 'opacity 0.5s ease-in-out',
                  maxWidth: '800px'  // Maximum width constraint
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* All Buttons and Links Section */}
      <section className="bg-gray-100 py-8 mt-24 -mb-8">
        <div className="container mx-auto px-4">
          {/* Start Exploring Materials Button */}
          <div
            className="bg-white rounded-lg p-6 shadow-lg cursor-pointer hover:shadow-xl transition-shadow"
            onClick={() => navigate('/search')}
          >
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-semibold text-red-900">Catalyst Materials</h2>
                <p className="text-gray-600">Begin your journey exploring materials and their catalytic performance</p>
              </div>
              <img
                src="/images/materials_icon.jpeg"
                alt="Materials"
                className="w-16 h-16 object-contain"
              />
            </div>
          </div>

          {/* Publications Button */}
          <div
            className="bg-white rounded-lg p-6 shadow-lg cursor-pointer hover:shadow-xl transition-shadow mt-8"
            onClick={() => navigate('/publications')}
          >
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-semibold text-red-900">Publications</h2>
                <p className="text-gray-600">Access our collection of research publications and associated datasets</p>
              </div>
              <img src="/images/publication_icon.jpeg" alt="Publications" className="w-16 h-16 object-contain" />
            </div>
          </div>

          <div className="mt-8 space-y-2 mb-16">
          {/* How to cite section */}
          <div className="bg-white rounded-lg shadow hover:shadow-md transition-shadow overflow-hidden">
            <button 
              onClick={() => setExpandedSections(prev => ({ ...prev, cite: !prev.cite }))}
              className="w-full p-4 text-left flex justify-between items-center"
            >
              <h3 className="text-red-900 font-semibold">How to cite</h3>
              <span className={`transform transition-transform duration-200 ${
                expandedSections.cite ? 'rotate-180' : ''
              }`}>▼</span>
            </button>
            <div className={`overflow-hidden transition-all duration-300 ${
              expandedSections.cite ? 'max-h-96 p-4 border-t' : 'max-h-0'
            }`}>
              <p className="text-gray-700">
              <p className="text-black font-bold">
                  When referring to the Experimental-Catalysis-Hub platform in general, please cite the following publication:
              </p>
              </p>
              <p className="mt-4 text-gray-800 italic">
              <p className="mb-2">Ruchika Mahajan; Ashton M. Aleman; Colin F. Crago; Suman Bhasker-Ranganath; Melissa E. Kreider; Jose A. Zamora Zeledon; Johanna Schröder; Gaurav A. Kamat; McKenzie A. Hubert; Adam C. Nielander; Thomas F. Jaramillo; Michaela Burke Stevens; Johannes Voss; Kirsten T. Winther</p>
              <p className="font-semibold mb-1">A research database for experimental electrocatalysis: Advancing data sharing and reusability</p>
              <p className="italic mb-2">J. Chem. Phys. 163, 124704 (2025)</p>
              <a href="https://doi.org/10.1063/5.0280821" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-medium">https://doi.org/10.1063/5.0280821</a>
         
              </p>
              <div className="mt-6 bg-red-50 p-4 rounded border border-red-800">
                <p className="font-semibold mb-2">Citation Guidelines:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Each dataset on this platform is linked to a specific publication</li>
                  <li>If you use data from this website, please also cite the related publication.</li>
                  <li>You can find the DOI link with each dataset to access and cite the original research</li>
                </ul>
              </div>
            </div>

          </div>

          {/* CatHub Experimental Overview section */}
          <div className="bg-white rounded-lg shadow hover:shadow-md transition-shadow overflow-hidden">
            <button 
              onClick={() => setExpandedSections(prev => ({ ...prev, overview: !prev.overview }))}
              className="w-full p-4 text-left flex justify-between items-center"
            >
              <h3 className="text-red-900 font-semibold">CatHub Experimental Overview</h3>
              <span className={`transform transition-transform duration-200 ${
                expandedSections.overview ? 'rotate-180' : ''
              }`}>▼</span>
            </button>
            <div className={`overflow-hidden transition-all duration-300 ${
              expandedSections.overview ? 'max-h-96 p-4 border-t' : 'max-h-0'
            }`}>
              <p className="text-gray-700">
                The CatHub Experimental Database is a comprehensive collection of experimental data
                for catalysis research. This includes detailed information about materials, reaction conditions,
                and performance metrics.
              </p>
              {/* Add more content as needed */}
            </div>
          </div>

          {/* Acknowledgement section */}
          <div className="bg-white rounded-lg shadow hover:shadow-md transition-shadow overflow-hidden">
            <button 
              onClick={() => setExpandedSections(prev => ({ ...prev, acknowledgement: !prev.acknowledgement }))}
              className="w-full p-4 text-left flex justify-between items-center"
            >
              <h3 className="text-red-900 font-semibold">Acknowledgement</h3>
              <span className={`transform transition-transform duration-200 ${
                expandedSections.acknowledgement ? 'rotate-180' : ''
              }`}>▼</span>
            </button>
            <div className={`overflow-hidden transition-all duration-300 ${
              expandedSections.acknowledgement ? 'max-h-96 p-4 border-t' : 'max-h-0'
            }`}>
              <p className="text-gray-700">
                This work was supported by various organizations and institutions. We would like to thank:
              </p>
              <ul className="list-disc ml-6 mt-2 text-gray-700">
                <li>SUNCAT Center for Interface Science and Catalysis</li>
                <li>SLAC National Accelerator Laboratory</li>
                <li>Stanford University</li>
                <li>Department of Energy (DOE) USA</li>
                {/* Add more acknowledgements as needed */}
              </ul>
            </div>
          </div>
        </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200">
        {/* Top footer with logos */}
        <div className="container mx-auto px-4 py-8">
            <div className="flex justify-between items-center">
                <div className="flex justify-between">
                    <img src="/images/suncat_logo.png" alt="SUNCAT" className="h-12 mr-40" />
                    <img src="/images/catalysis-hub-logo.png" alt="Catalysis Hub" className="h-16 mx-40 mr-40" />
                    <img src="/images/slac_logo.png" alt="SLAC" className="h-12 ml-35" />
                </div>
            </div>
        </div>

        {/* Bottom footer with Stanford branding and navigation */}
        <div className="bg-red-800 text-white py-16">
            <div className="container mx-auto px-4">
                <div className="flex items-center justify-between">
                    {/* Stanford University */}
                    <div className="flex items-center">
                        <span className="text-2xl font-serif">Stanford</span>
                        <span className="text-sm ml-1">University</span>
                    </div>

                    {/* Navigation Links */}
                    <div className="flex items-center space-x-8">
                        <Link to="/docs" className="flex items-center hover:opacity-80">
                            <span className="mr-3 text-2xl">📄</span> Docs
                        </Link>
                        <Link to="https://github.com" className="flex items-center hover:opacity-80">
                            <FaGithub className="mr-3 text-2xl" /> Visit us on GitHub
                        </Link>
                        <Link to="/license" className="flex items-center hover:opacity-80">
                            <span className="mr-3 text-2xl">©️</span> License
                        </Link>
                    </div>
                </div>
            </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;